/**
 * GEN-PX-034 · Página de un área de Saber (#/saber/area/<id>). Mockups: E14-M2 (por subáreas), E14-M2b (árbol y detalle de subárea).
 * Dos pestañas, «Por subáreas» (por defecto) y «Árbol»; pulsar una subárea entra en su detalle («← Área», vuelve a la pestaña donde se estaba).
 * Todo se calcula en el cliente con el `mapa` de GET /v1/ekb/saber (lib/saberArea.js); el kernel solo añade `descripcion`.
 * Las subáreas son áreas con `padre_id`: no hay agrupación de KBs aparte.
 */
import { useState } from 'react'
import { AccionesArea, FilaKbGestion, Ruta, frescura, plural } from './SaberMapa.jsx'
import { avisosDeArea, kbsDeRama, nivelFrescura, puedeVerAvisos, repartoFrescura, ultimoCambio, ultimosCambios } from '../lib/saberArea.js'

const KBS_EN_ARBOL = 4 // M2b: al desplegar una subárea salen 4 KBs y «Ver los N KBs de …»
const SUELTOS_VISIBLES = 5

/** «Actualizando…» junto al título mientras se revalida lo precargado; si falla, «Puede no estar al día · Reintentar». Nunca tapa contenido. */
export function Actualizando({ estado, onReintentar }) {
  if (estado === 'cargando') return <span className="dk-actualiza" role="status"><i aria-hidden="true" />Actualizando…</span>
  if (estado === 'fallo') {
    return (
      <span className="dk-actualiza dk-actualiza--fallo" role="status">
        Puede no estar al día · <button type="button" onClick={onReintentar}>Reintentar</button>
      </span>
    )
  }
  return null
}

const Punto = ({ iso }) => <i className={`dk-fresca dk-fresca--${nivelFrescura(iso)}`} aria-hidden="true" />
const Cifra = ({ rotulo, valor }) => <div className="dk-ar__cifra"><span>{rotulo}</span><b>{valor}</b></div>
const fmt = n => Number(n ?? 0).toLocaleString('es-ES', { useGrouping: 'always' })

function BarraFrescura({ kbs }) {
  const r = repartoFrescura(kbs)
  if (!r.total) return null
  const tramos = [['verde', 'Al día', r.verde], ['ambar', '1-3 meses', r.ambar], ['gris', 'Más de 3 meses', r.gris]]
  return (
    <div className="dk-ar__fresca" aria-label="Frescura de los KBs">
      <div className="dk-ar__barra" aria-hidden="true">
        {tramos.map(([n, , v]) => v > 0 && <span key={n} className={`dk-ar__tramo dk-ar__tramo--${n}`} style={{ flexGrow: v }} />)}
      </div>
      <div className="dk-ar__leyenda">
        {tramos.map(([n, t, v]) => <span key={n}><i className={`dk-fresca dk-fresca--${n}`} aria-hidden="true" />{t} · {plural(v, 'KB', 'KBs')}</span>)}
      </div>
    </div>
  )
}

const Insignias = ({ lista }) => (lista ?? []).map(b => <span key={b.clave + b.texto} className="dk-ar__aviso">{b.texto}</span>)

function FilaArbolKb({ kb, onAbrir, insignias, sangria = true }) {
  return (
    <button type="button" className={`dk-ar__fila${sangria ? ' dk-ar__fila--kb' : ''}`} onClick={() => onAbrir(kb)}>
      <Punto iso={kb.actualizado} />
      <span className="dk-ar__nombre">{kb.nombre}</span>
      <Insignias lista={insignias} />
      <span className="dk-ar__m">{plural(kb.ikus, 'iKu', 'iKus')}</span>
    </button>
  )
}

function PorSubareas({ area, subs, mapa, sueltos, onVerArea, onAbrirKb, gestion }) {
  const [todos, setTodos] = useState(false)
  const kbs = kbsDeRama(area, mapa)
  const cambios = ultimosCambios(area, mapa)
  const visibles = todos || !subs.length ? sueltos : sueltos.slice(0, SUELTOS_VISIBLES) // sin subáreas, esos son todos los KBs del área
  return (
    <>
      <BarraFrescura kbs={kbs} />
      {(subs.length > 0 || sueltos.length > 0) && (
        <div className="dk-ar__tarjetas" role="group" aria-label="Subáreas">
          {subs.map(s => {
            const ks = kbsDeRama(s, mapa)
            const u = ultimoCambio(ks)
            const f = frescura(u)
            return (
              <button key={s.id} type="button" className="dk-ar__sub" onClick={() => onVerArea(s.id)} aria-label={`Abrir la subárea ${s.nombre}`}>
                <span className="dk-ar__sub-t"><span aria-hidden="true">{s.icono}</span> {s.nombre}</span>
                <span className="dk-ar__m">{plural(s.ikus, 'iKu', 'iKus')} · {plural(ks.length, 'KB', 'KBs')}</span>
                <span className="dk-ar__m">{f ? <><Punto iso={u} />Último cambio {f.texto}</> : 'Sin cambios todavía'}</span>
              </button>
            )
          })}
          {sueltos.length > 0 && (
            <div className="dk-ar__sub dk-ar__sub--sueltos" role="group" aria-label={subs.length ? 'Sin subárea' : 'KBs del área'}>
              <span className="dk-ar__sub-t">{subs.length ? 'Sin subárea' : 'KBs del área'}</span>
              <span className="dk-ar__m">{plural(sueltos.length, 'KB', 'KBs')}</span>
              <div className="dk-ar__sueltos">
                {visibles.map(k => <FilaKbGestion key={k.id} k={k} areaId={area.id} onAbrirKb={onAbrirKb} gestion={gestion} />)}
              </div>
              {subs.length > 0 && sueltos.length > SUELTOS_VISIBLES && (
                <button type="button" className="dk-area__ver" onClick={() => setTodos(t => !t)}>{todos ? 'Ver menos' : `Ver los ${sueltos.length} KBs →`}</button>
              )}
            </div>
          )}
        </div>
      )}
      {subs.length === 0 && sueltos.length === 0 && <p className="dk-area__vacia">Sin KBs todavía</p>}
      {cambios.length > 0 && (
        <section className="dk-ar__cambios" aria-label="Últimos cambios en el área">
          <h3>Últimos cambios en el área</h3>
          {cambios.map(({ kb, sub }) => (
            <button key={kb.id} type="button" className="dk-ar__fila" onClick={() => onAbrirKb(kb)}>
              <Punto iso={kb.actualizado} />
              <span className="dk-ar__nombre">{kb.nombre}</span>
              {sub && <span className="dk-ar__m">{sub.nombre}</span>}
              <span className="dk-ar__m">{frescura(kb.actualizado)?.texto}</span>
            </button>
          ))}
        </section>
      )}
    </>
  )
}

function Arbol({ subs, mapa, sueltos, avisos, verAvisos, abiertos, onAbiertos, onVerArea, onAbrirKb }) {
  const insig = id => (verAvisos ? avisos.porKb.get(id) : null)
  const alternar = id => onAbiertos(abiertos.includes(id) ? abiertos.filter(x => x !== id) : [...abiertos, id])
  return (
    <>
      {verAvisos && avisos.hay && (
        <p className="dk-ar__franja" role="status">⚠ Para el responsable: {avisos.resumen.map(r => r.texto).join(' · ')}</p>
      )}
      <div className="dk-ar__arbol">
        {subs.map(s => {
          const ks = kbsDeRama(s, mapa)
          const u = ultimoCambio(ks)
          const abierta = abiertos.includes(s.id)
          const propios = verAvisos ? avisos.porSub.get(s.id) : null
          const nAv = verAvisos ? avisos.totalSub[s.id] : 0
          const mostrados = [...ks].sort((a, b) => (avisos.porKb.get(b.id)?.length ?? 0) - (avisos.porKb.get(a.id)?.length ?? 0))
          return (
            <div key={s.id} className="dk-ar__rama">
              <div className="dk-ar__fila dk-ar__fila--sub">
                <button type="button" className="dk-ar__flecha" aria-expanded={abierta} aria-label={`${abierta ? 'Plegar' : 'Desplegar'} ${s.nombre}`} onClick={() => alternar(s.id)}>{abierta ? '▾' : '▸'}</button>
                <Punto iso={u} />
                <button type="button" className="dk-ar__nombre dk-ar__nombre--sub" onClick={() => onVerArea(s.id)}><span aria-hidden="true">{s.icono}</span> {s.nombre}</button>
                <Insignias lista={propios} />
                {nAv > 0 && !propios?.length && <span className="dk-ar__aviso">{nAv} {nAv === 1 ? 'aviso' : 'avisos'}</span>}
                <span className="dk-ar__m">{plural(s.ikus, 'iKu', 'iKus')} · {plural(ks.length, 'KB', 'KBs')}</span>
              </div>
              {abierta && (
                <>
                  {mostrados.slice(0, KBS_EN_ARBOL).map(k => <FilaArbolKb key={k.id} kb={k} onAbrir={onAbrirKb} insignias={insig(k.id)} />)}
                  {ks.length > KBS_EN_ARBOL && (
                    <button type="button" className="dk-ar__fila dk-ar__fila--kb dk-ar__mas" onClick={() => onVerArea(s.id)}>Ver los {ks.length} KBs de {s.nombre}</button>
                  )}
                </>
              )}
            </div>
          )
        })}
        {sueltos.map(k => (
          <FilaArbolKb key={k.id} kb={k} onAbrir={onAbrirKb} sangria={false}
            insignias={subs.length ? (verAvisos ? avisos.porKb.get(k.id) : [{ clave: 'sinSubarea', texto: 'Sin subárea' }]) : insig(k.id)} />
        ))}
        {subs.length === 0 && sueltos.length === 0 && <p className="dk-area__vacia">Sin KBs todavía</p>}
      </div>
      <p className="dk-ar__pie">Pulsa el nombre de una subárea para entrar · la flecha la despliega aquí · punto: verde al día, ámbar 1-3 meses, gris más de 3 meses</p>
    </>
  )
}

/** Detalle de una subárea sin subáreas propias (M2b): cifras, KBs con aviso primero, o el texto de subárea vacía. */
function DetalleSubarea({ area, mapa, avisos, verAvisos, onAbrirKb, gestion }) {
  const kbs = kbsDeRama(area, mapa)
  const nAv = (kbs.reduce((t, k) => t + (avisos.porKb.get(k.id)?.length ?? 0), 0)) + (avisos.porSub.get(area.id)?.length ?? 0)
  const orden = [...kbs].sort((a, b) => (avisos.porKb.get(b.id)?.length ? 1 : 0) - (avisos.porKb.get(a.id)?.length ? 1 : 0))
  return (
    <>
      <div className="dk-ar__cifras">
        <Cifra rotulo="iKus vigentes" valor={fmt(area.ikus)} />
        <Cifra rotulo="KBs" valor={fmt(kbs.length)} />
        {verAvisos && <Cifra rotulo="Avisos" valor={fmt(nAv)} />}
      </div>
      {kbs.length === 0 ? (
        <p className="dk-ar__vacia">Esta subárea no tiene KBs. Pídele a Jimmy que aprenda lo que sabemos de {area.nombre.toLowerCase()} o que mueva aquí los KBs que correspondan.</p>
      ) : (
        <div className="dk-ar__lista">
          {orden.map(k => (
            <div key={k.id} className="dk-ar__kb">
              <FilaKbGestion k={k} areaId={area.id} onAbrirKb={onAbrirKb} gestion={gestion} />
              {verAvisos && <div className="dk-ar__badges"><Insignias lista={avisos.porKb.get(k.id)} /></div>}
            </div>
          ))}
        </div>
      )}
      {verAvisos && kbs.length > 0 && <p className="dk-ar__pie">Los KBs con aviso salen primero.</p>}
    </>
  )
}

/**
 * `vista` = { pestana, abiertos } guardado por SaberPanel para este área (sobrevive a entrar en una subárea y volver); `onVista(parche)` lo actualiza.
 * `actualizacion` = { estado: 'cargando' | 'fallo' | 'ok', onReintentar }. `rol` / `email`: quien mira (los avisos son del responsable).
 */
export function SaberArea({ area, mapa = [], onAbrirKb, onVolver, onVerArea, gestion = null, acciones = {}, vista, onVista, actualizacion, rol, email }) {
  const [pestana, setPestana] = useState(vista?.pestana ?? 'subareas')
  const [abiertos, setAbiertos] = useState(vista?.abiertos ?? [])
  const elegir = p => { setPestana(p); onVista?.({ pestana: p }) }
  const abrir = l => { setAbiertos(l); onVista?.({ abiertos: l }) }

  const ancestros = []
  for (let p = area?.padre_id; p && ancestros.length < 20;) {
    const a = mapa.find(x => x.id === p)
    if (!a) break
    ancestros.unshift(a)
    p = a.padre_id
  }
  const padre = ancestros.at(-1) ?? null
  const subs = area ? mapa.filter(a => a.padre_id === area.id) : []
  const esDetalle = Boolean(area && padre && subs.length === 0)
  const sueltos = area?.kbs ?? [] // los «también en esta área» se ven, pero no cuentan como «sin subárea» (lib/saberArea.js)
  // En el detalle de una subárea los avisos se calculan desde su área (sus KBs se comparan con los hermanos).
  const avisos = area ? avisosDeArea(esDetalle ? padre : area, mapa) : null
  const verAvisos = Boolean(area) && puedeVerAvisos(area, mapa, rol, email)
  const volver = esDetalle ? () => onVerArea?.(padre.id) : onVolver
  const rutaIds = ancestros.length === area?.ruta?.length ? ancestros.map(a => a.id) : null

  return (
    <section className="dk-mapa dk-saber__area" aria-label={area ? `Área ${area.nombre}` : 'Área'}>
      {/* GEN-PX-020g: acciones del área en la línea de «← …», a la derecha, como en la portada (.dk-mapa__cab + acts) */}
      <div className="dk-mapa__cab dk-area__barra">
        <button type="button" className="dk-mapa__volver" onClick={volver}>{esDetalle ? `← ${padre.nombre}` : '← Lo que sabemos'}</button>
        {area && <div className="dk-mapa__acts dk-area__acts"><AccionesArea area={area} gestion={gestion} acciones={acciones} /></div>}
      </div>
      {!area && <p className="dk-saber__aviso" role="status">Esta área no está disponible para ti.</p>}
      {area && (
        <div className="dk-area dk-ar">
          <div className="dk-ar__cab">
            <div className="dk-area__ico" aria-hidden="true">{area.icono}</div>
            <div className="dk-ar__titulo">
              <div className="dk-rotulo">{padre ? 'Subárea' : 'Área'}</div>
              {esDetalle
                ? (
                  <nav className="dk-area__ruta" aria-label="Ruta">
                    <button type="button" onClick={onVolver}>Lo que sabemos</button>{' › '}
                    <Ruta ruta={ancestros.map(a => a.nombre)} ids={ancestros.map(a => a.id)} onIr={onVerArea} />
                  </nav>
                )
                : <Ruta ruta={ancestros.length ? ancestros.map(a => a.nombre) : area.ruta} ids={rutaIds} onIr={onVerArea} />}
              <h2>{area.nombre}</h2>
            </div>
            <Actualizando {...(actualizacion ?? {})} />
          </div>
          <p className="dk-ar__desc">{area.descripcion || (esDetalle ? 'Sin descripción todavía.' : '')}</p>
          {esDetalle ? (
            <DetalleSubarea area={area} mapa={mapa} avisos={avisos} verAvisos={verAvisos} onAbrirKb={onAbrirKb} gestion={gestion} />
          ) : (
            <>
              <div className="dk-ar__cifras">
                <Cifra rotulo="iKus vigentes" valor={fmt(area.ikus)} />
                <Cifra rotulo="KBs" valor={fmt(kbsDeRama(area, mapa).length)} />
                <Cifra rotulo="Subáreas" valor={fmt(subs.length)} />
              </div>
              <div className="dk-ar__pestanas" role="tablist" aria-label="Vista del área">
                {[['subareas', 'Por subáreas'], ['arbol', 'Árbol']].map(([id, t]) => (
                  <button key={id} type="button" role="tab" aria-selected={pestana === id} className={pestana === id ? 'dk-ar__pestana dk-ar__pestana--activa' : 'dk-ar__pestana'} onClick={() => elegir(id)}>{t}</button>
                ))}
              </div>
              <div role="tabpanel" aria-label={pestana === 'arbol' ? 'Árbol' : 'Por subáreas'}>
                {pestana === 'arbol'
                  ? <Arbol subs={subs} mapa={mapa} sueltos={sueltos} avisos={avisos} verAvisos={verAvisos} abiertos={abiertos} onAbiertos={abrir} onVerArea={onVerArea} onAbrirKb={onAbrirKb} />
                  : <PorSubareas area={area} subs={subs} mapa={mapa} sueltos={sueltos} onVerArea={onVerArea} onAbrirKb={onAbrirKb} gestion={gestion} />}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  )
}
